/** @jsxImportSource theme-ui */

import React from 'react';
import { H1, H2, H3, H4 } from '../Heading';
import MarkdownToJsx from 'markdown-to-jsx';
import { TextLink } from '../Link';
import FlexContainer from '../FlexContainer';
import Text from '../Text';
import { Divider } from '../Divider';
import { CarrotTheme } from '../../theme';

interface ElementProps {
    id?: string;
    children?: React.ReactNode | React.ReactNode[];
}

interface AnchorElementProps extends ElementProps {
    href: string;
    title?: string;
    target?: string;
}

export interface MarkdownProps {
    source: string;
    styleOverrides?: object;
    as?: React.ElementType;
}

const elements = {
    h1({ id, children }: ElementProps) {
        return <H1 id={id}>{children}</H1>;
    },
    h2({ id, children }: ElementProps) {
        return <H2 id={id}>{children}</H2>;
    },
    h3({ id, children }: ElementProps) {
        return <H3 id={id}>{children}</H3>;
    },
    h4({ id, children }: ElementProps) {
        return <H4 id={id}>{children}</H4>;
    },
    p({ children }: ElementProps) {
        return (
            <Text as="p" color="black80">
                {children}
            </Text>
        );
    },
    strong({ children }: ElementProps) {
        return <Text weight="bold">{children}</Text>;
    },
    em({ children }: ElementProps) {
        return <Text fontStyle="italic">{children}</Text>;
    },
    a({ href, title, target = '_blank', children }: AnchorElementProps) {
        return (
            <TextLink size="medium" target={target} title={title} to={href} sx={{ display: 'inline-block' }}>
                {children}
            </TextLink>
        );
    },
    ol({ children }: ElementProps) {
        return (
            <ol sx={{ margin: '16px 0px 16px -24px', paddingLeft: '40px', listStyle: 'decimal' }}>
                <FlexContainer direction="column" gap="tiny" paddingLeftRight="small">
                    {children}
                </FlexContainer>
            </ol>
        );
    },
    ul({ children }: ElementProps) {
        return (
            <ul sx={{ margin: '16px 0px 16px -24px', paddingLeft: '40px', listStyleType: 'initial' }}>
                <FlexContainer direction="column" gap="tiny" paddingLeftRight="small">
                    {children}
                </FlexContainer>
            </ul>
        );
    },
    li({ children }: ElementProps) {
        return (
            <li sx={{ display: 'list-item' }}>
                <Text as="span" color="black-80">
                    {children}
                </Text>
            </li>
        );
    },
    table({ children }: ElementProps) {
        return (
            <table sx={{ borderCollapse: 'collapse', width: '100%', textAlign: 'left', margin: '48px 0px' }}>
                {children}
            </table>
        );
    },
    th({ children }: ElementProps) {
        return (
            <th sx={{ padding: (theme: CarrotTheme) => theme.space.small, border: '1px solid #ccc' }}>
                <Text weight="semibold">{children}</Text>
            </th>
        );
    },
    td({ children }: ElementProps) {
        return (
            <td sx={{ padding: (theme: CarrotTheme) => theme.space.small, border: '1px solid #ccc' }}>
                <Text>{children}</Text>
            </td>
        );
    },
    hr() {
        return <Divider color="black10" />;
    },
};

export function Markdown({ source, styleOverrides, as }: MarkdownProps): JSX.Element {
    return (
        <MarkdownToJsx
            options={{
                overrides: {
                    ...elements,
                    ...styleOverrides,
                },
                forceBlock: true,
                wrapper: as,
            }}
        >
            {source}
        </MarkdownToJsx>
    );
}

Markdown.defaultProps = {
    as: 'div',
    styleOverrides: {},
};
